import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  useColorMode,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IoMdMoon, IoMdSunny } from 'react-icons/io';
import {
  MdArrowBack,
  MdCheckCircle,
  MdFullscreen,
  MdFullscreenExit,
  MdSave,
  MdWarning,
} from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';

import {
  useGetSeatingPlan,
  useUpdateSeatingPlan,
} from 'queries/seatingPlan.query';
import { Canvas } from './Canvas';
import { Sidebar } from './Sidebar';
import { Toolbar } from './Toolbar';
import { UnsavedChangesModal } from './UnsavedChangesModal';
import { ValidationModal } from './ValidationModal';
import { AutoLabelModal } from './AutoLabelModal';
import {
  autoResolveDuplicateRowNumbers,
  calculateRowSeatPositions,
  deduplicateSeatGuids,
  purgeEmptyRows,
  generateSeatNum,
  generateUniqueSeatGuid,
  validatePlan,
} from './planUtils';

export default function SeatingPlanEditorView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const fileInputRef = useRef(null);

  const topBarBg = useColorModeValue('#FFFFFF', '#0F172A');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const inputColor = useColorModeValue('gray.800', 'white');
  const { colorMode, toggleColorMode } = useColorMode();

  const { data: planData } = useGetSeatingPlan(id);
  const updatePlanMutation = useUpdateSeatingPlan();

  // Main Seating Plan Data State
  const [plan, setPlan] = useState(null);
  const [activeZoneIndex, setActiveZoneIndex] = useState(0);

  // Selection states
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [selectedSeatGuids, setSelectedSeatGuids] = useState([]);
  const [selectedAreaIndices, setSelectedAreaIndices] = useState([]);

  // Active Tool: 'select' (V), 'seat-select' (S), 'add-rows' (B), 'add-row' (N), 'add-seat' (M),
  //              'add-rect' (R), 'add-circle' (C), 'add-ellipse' (E), 'add-polygon' (P), 'add-text' (T), 'pan'
  const [activeTool, setActiveTool] = useState('select');

  // History for Undo / Redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const historyRef = useRef({ history: [], index: -1 });
  const [lastSavedHistoryIndex, setLastSavedHistoryIndex] = useState(-1);

  // Unsaved Changes Modal state
  const {
    isOpen: isUnsavedModalOpen,
    onOpen: onOpenUnsavedModal,
    onClose: onCloseUnsavedModal,
  } = useDisclosure();
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Clipboard for Cut, Copy, Paste
  const [clipboard, setClipboard] = useState(null);

  // Viewport Zoom & Pan
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 50, y: 30 });

  // Viewport Centering & Zoom Handlers
  const handleCenterView = useCallback((forcedZoom = null) => {
    const currentZone = plan?.zones?.[activeZoneIndex];
    const rows = currentZone?.rows || [];
    const areas = currentZone?.areas || [];

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    const addPoint = (px, py) => {
      if (px < minX) minX = px;
      if (px > maxX) maxX = px;
      if (py < minY) minY = py;
      if (py > maxY) maxY = py;
    };

    rows.forEach((r) => {
      const rx = r.position?.x || 0;
      const ry = r.position?.y || 0;
      const rRad = ((r.rotation || 0) * Math.PI) / 180;
      const cos = Math.cos(rRad);
      const sin = Math.sin(rRad);

      (r.seats || []).forEach((s) => {
        const lx = s.position?.x || 0;
        const ly = s.position?.y || 0;
        const radius = s.radius || r.seat_radius || 10;
        const absX = rx + (lx * cos - ly * sin);
        const absY = ry + (lx * sin + ly * cos);

        addPoint(absX - radius, absY - radius);
        addPoint(absX + radius, absY + radius);
      });

      if (!r.seats || r.seats.length === 0) {
        addPoint(rx, ry);
      }
    });

    areas.forEach((a) => {
      const ax = a.position?.x || 0;
      const ay = a.position?.y || 0;
      const aRad = ((a.rotation || 0) * Math.PI) / 180;
      const cos = Math.cos(aRad);
      const sin = Math.sin(aRad);

      if (a.shape === 'rectangle' && a.rectangle) {
        const w = a.rectangle.width || 60;
        const h = a.rectangle.height || 60;
        [[-w / 2, -h / 2], [w / 2, -h / 2], [w / 2, h / 2], [-w / 2, h / 2]].forEach(([dx, dy]) => {
          addPoint(ax + dx * cos - dy * sin, ay + dx * sin + dy * cos);
        });
      } else if (a.shape === 'circle' && a.circle) {
        const r = a.circle.radius || 30;
        addPoint(ax - r, ay - r);
        addPoint(ax + r, ay + r);
      } else if (a.shape === 'ellipse' && a.ellipse) {
        const rx = a.ellipse.radius?.x || 50;
        const ry = a.ellipse.radius?.y || 30;
        [[-rx, -ry], [rx, -ry], [rx, ry], [-rx, ry]].forEach(([dx, dy]) => {
          addPoint(ax + dx * cos - dy * sin, ay + dx * sin + dy * cos);
        });
      } else if (a.shape === 'text' && a.text) {
        const fs = a.text.fontSize || 16;
        const tw = Math.max(40, (a.text.text?.length || 4) * (fs * 0.65));
        const th = fs * 1.3;
        [[-tw / 2, -th / 2], [tw / 2, -th / 2], [tw / 2, th / 2], [-tw / 2, th / 2]].forEach(([dx, dy]) => {
          addPoint(ax + dx * cos - dy * sin, ay + dx * sin + dy * cos);
        });
      } else if (a.shape === 'polygon' && a.polygon?.points) {
        (a.polygon.points || []).forEach((p) => {
          addPoint(ax + p.x * cos - p.y * sin, ay + p.x * sin + p.y * cos);
        });
      } else {
        addPoint(ax - 30, ay - 30);
        addPoint(ax + 30, ay + 30);
      }
    });

    const planW = plan?.size?.width || 900;
    const planH = plan?.size?.height || 900;
    if (minX === Infinity || maxX === -Infinity) {
      minX = 0;
      maxX = planW;
      minY = 0;
      maxY = planH;
    }

    const contentW = Math.max(100, maxX - minX);
    const contentH = Math.max(100, maxY - minY);
    const contentCenterX = minX + contentW / 2;
    const contentCenterY = minY + contentH / 2;

    const container = document.getElementById('seating-plan-canvas-svg');
    const rect = container ? container.getBoundingClientRect() : { width: 1000, height: 700 };
    const vpW = rect.width > 50 ? rect.width : 1000;
    const vpH = rect.height > 50 ? rect.height : 700;

    let targetZoom;
    if (forcedZoom !== null) {
      targetZoom = forcedZoom;
    } else {
      const pad = 60;
      const fitZoomX = (vpW - pad * 2) / contentW;
      const fitZoomY = (vpH - pad * 2) / contentH;
      targetZoom = Math.max(0.15, Math.min(fitZoomX, fitZoomY, 1.5));
    }

    const newPanX = (vpW / 2) - (contentCenterX * targetZoom);
    const newPanY = (vpH / 2) - (contentCenterY * targetZoom);

    setZoom(Math.round(targetZoom * 100) / 100);
    setPan({ x: Math.round(newPanX), y: Math.round(newPanY) });
  }, [plan, activeZoneIndex]);

  const handleZoomIn = useCallback(() => {
    const container = document.getElementById('seating-plan-canvas-svg');
    const rect = container ? container.getBoundingClientRect() : { width: 1000, height: 700 };
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const nextZoom = Math.min(zoom * 1.2, 5);
    const scale = nextZoom / zoom;
    const newPanX = cx - (cx - pan.x) * scale;
    const newPanY = cy - (cy - pan.y) * scale;
    setZoom(nextZoom);
    setPan({ x: Math.round(newPanX), y: Math.round(newPanY) });
  }, [zoom, pan]);

  const handleZoomOut = useCallback(() => {
    const container = document.getElementById('seating-plan-canvas-svg');
    const rect = container ? container.getBoundingClientRect() : { width: 1000, height: 700 };
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const nextZoom = Math.max(zoom / 1.2, 0.2);
    const scale = nextZoom / zoom;
    const newPanX = cx - (cx - pan.x) * scale;
    const newPanY = cy - (cy - pan.y) * scale;
    setZoom(nextZoom);
    setPan({ x: Math.round(newPanX), y: Math.round(newPanY) });
  }, [zoom, pan]);

  // Grid Snap toggle
  const [snapToGrid, setSnapToGrid] = useState(true);

  // Fullscreen toggle
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Validation Modal disclosure
  const {
    isOpen: isValidationOpen,
    onOpen: onOpenValidation,
    onClose: onCloseValidation,
  } = useDisclosure();

  // Auto Label Rows & Seats Modal disclosure
  const {
    isOpen: isAutoLabelOpen,
    onOpen: onOpenAutoLabel,
    onClose: onCloseAutoLabel,
  } = useDisclosure();

  // Selected row indices in current zone
  const selectedRowIndices = useMemo(() => {
    if (!plan || !plan.zones || !plan.zones[activeZoneIndex]) return [];
    const currentZone = plan.zones[activeZoneIndex];
    const selectedSet = new Set(selectedSeatGuids);
    const indices = [];
    (currentZone.rows || []).forEach((row, idx) => {
      if (idx === selectedRowIndex) {
        indices.push(idx);
      } else if (
        (row.seats || []).length > 0 &&
        (row.seats || []).some((s) => selectedSet.has(s.seat_guid))
      ) {
        indices.push(idx);
      }
    });
    return indices;
  }, [plan, activeZoneIndex, selectedSeatGuids, selectedRowIndex]);

  // Validation Results calculation
  const validationErrors = useMemo(() => validatePlan(plan), [plan]);

  const lastLoadedIdRef = useRef(null);

  // Initialize plan from API
  useEffect(() => {
    if (planData?.data || planData) {
      const raw = planData.data || planData;
      
      // Prevent background refetches (e.g. after a Save) from wiping out 
      // the user's unsaved changes and resetting the history stack.
      if (lastLoadedIdRef.current === raw.id) {
        return;
      }

      const initialized = {
        id: raw.id,
        name: raw.name || 'Untitled Seating Plan',
        background_image_url: raw.background_image_url || '',
        background_settings: raw.background_settings || {
          opacity: 0.8,
          x: 0,
          y: 0,
          width: 900,
          height: 900,
        },
        categories: (raw.plan_json?.categories || [
          { name: 'Category I', color: '#E03400' },
          { name: 'Category II', color: '#BB36D3' },
          { name: 'Category III', color: '#3A863D' },
          { name: 'category222', color: '#3FFF00' },
        ]).map(cat => ({
          ...cat,
          uuid: cat.uuid || Math.random().toString(36).substring(2, 9)
        })),
        zones: raw.plan_json?.zones || [
          {
            name: 'Main Floor',
            position: { x: 0, y: 0 },
            rows: [],
            areas: [],
          },
        ],
        size: raw.plan_json?.size || { width: 900, height: 900 },
      };
      // Purge ghost rows (0 seats) and deduplicate any duplicate seat IDs if present in loaded plan
      const withoutEmptyRows = purgeEmptyRows(initialized);
      const cleaned = deduplicateSeatGuids(withoutEmptyRows);
      setPlan(cleaned);
      setHistory([cleaned]);
      setHistoryIndex(0);
      historyRef.current = { history: [cleaned], index: 0 };
      setLastSavedHistoryIndex(0);
      lastLoadedIdRef.current = raw.id;
    }
  }, [planData]);

  // Record history snapshot
  const pushHistory = useCallback(
    (nextPlan) => {
      const { history: currHistory, index: currIndex } = historyRef.current;
      
      // Prevent pushing duplicate states (e.g. from stray clicks on canvas)
      if (currHistory.length > 0 && currIndex >= 0) {
        const currentPlan = currHistory[currIndex];
        if (currentPlan === nextPlan || JSON.stringify(currentPlan) === JSON.stringify(nextPlan)) {
          return;
        }
      }

      const upToCurrent = currHistory.slice(0, currIndex + 1);
      const newHistory = [...upToCurrent, nextPlan];
      const newIndex = currIndex + 1;
      
      historyRef.current = { history: newHistory, index: newIndex };
      
      setHistory(newHistory);
      setHistoryIndex(newIndex);
      setPlan(nextPlan);
    },
    [],
  );

  // Handle Apply Auto Labeling
  const handleApplyAutoLabels = useCallback(
    (updatedPlan, { rowCount, seatCount }) => {
      pushHistory(updatedPlan);
      toast({
        title: 'Auto Labeling Applied',
        description: `Updated labels and numbers for ${rowCount} rows (${seatCount} seats).`,
        status: 'success',
        duration: 3000,
      });
    },
    [pushHistory, toast],
  );

  // Undo
  const handleUndo = useCallback(() => {
    const { history: currHistory, index: currIndex } = historyRef.current;
    if (currIndex > 0) {
      const newIndex = currIndex - 1;
      const prevPlan = currHistory[newIndex];
      
      historyRef.current = { ...historyRef.current, index: newIndex };
      setHistoryIndex(newIndex);
      setPlan(prevPlan);
      // Clear selections to prevent out-of-bounds crashes
      setSelectedAreaIndices([]);
      setSelectedRowIndex(null);
      setSelectedSeatGuids([]);
      setActiveZoneIndex((prev) => Math.min(prev, Math.max(0, (prevPlan.zones?.length || 1) - 1)));
    }
  }, []);

  // Redo
  const handleRedo = useCallback(() => {
    const { history: currHistory, index: currIndex } = historyRef.current;
    if (currIndex < currHistory.length - 1) {
      const newIndex = currIndex + 1;
      const nextPlan = currHistory[newIndex];
      
      historyRef.current = { ...historyRef.current, index: newIndex };
      setHistoryIndex(newIndex);
      setPlan(nextPlan);
      // Clear selections
      setSelectedAreaIndices([]);
      setSelectedRowIndex(null);
      setSelectedSeatGuids([]);
      setActiveZoneIndex((prev) => Math.min(prev, Math.max(0, (nextPlan.zones?.length || 1) - 1)));
    }
  }, []);

  // Save Plan to API
  const handleSave = useCallback(async () => {
    if (!plan || !plan.id) return false;

    try {
      const cleanedPlan = deduplicateSeatGuids(plan);
      let totalSeats = 0;
      (cleanedPlan.zones || []).forEach((z) => {
        (z.rows || []).forEach((r) => {
          totalSeats += (r.seats || []).length;
        });
      });

      const payload = {
        name: cleanedPlan.name,
        plan_json: {
          name: cleanedPlan.name,
          categories: cleanedPlan.categories,
          zones: (cleanedPlan.zones || []).map((zone) => ({
            ...zone,
            rows: (zone.rows || [])
              .filter((row) => (row.seats || []).length > 0)
              .map((row) => ({
                ...row,
                seats: (row.seats || []).map((seat) =>
                  Object.fromEntries(Object.entries(seat).filter(([key]) => key !== 'status')),
                ),
              })),
          })),
          size: cleanedPlan.size,
        },
        background_image_url: cleanedPlan.background_image_url || null,
        background_settings: cleanedPlan.background_settings,
        total_seats: totalSeats,
      };

      await updatePlanMutation.mutateAsync({ id: plan.id, data: payload });
      setLastSavedHistoryIndex(historyIndex);
      toast({
        title: 'Plan saved successfully',
        description: `Seating plan updated (${totalSeats} seats)`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      return true;
    } catch (err) {
      toast({
        title: 'Failed to save seating plan',
        description: err?.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return false;
    }
  }, [plan, updatePlanMutation, toast, historyIndex]);

  // Compute isDirty state
  const isDirty = historyIndex !== lastSavedHistoryIndex;

  // beforeunload listener for browser exit confirmation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Handle back button with unsaved changes guard
  const handleBackNavigation = useCallback(() => {
    if (isDirty) {
      setPendingNavigation(() => () => navigate('/admin/seating-plans'));
      onOpenUnsavedModal();
    } else {
      navigate('/admin/seating-plans');
    }
  }, [isDirty, navigate, onOpenUnsavedModal]);

  // Save & Exit handler
  const handleSaveAndExit = useCallback(async () => {
    const success = await handleSave();
    if (success && pendingNavigation) {
      pendingNavigation();
      onCloseUnsavedModal();
    }
  }, [handleSave, pendingNavigation, onCloseUnsavedModal]);

  // Discard & Exit handler
  const handleDiscardAndExit = useCallback(() => {
    if (pendingNavigation) {
      pendingNavigation();
      onCloseUnsavedModal();
    }
  }, [pendingNavigation, onCloseUnsavedModal]);

  // New Blank Plan
  const handleNewPlan = () => {
    if (
      !window.confirm(
        'Create a new blank seating plan? Unsaved changes will be cleared.',
      )
    )
      return;
    const blank = {
      ...plan,
      name: 'New Plan',
      categories: [
        { name: 'Category I', color: '#E03400' },
        { name: 'Category II', color: '#BB36D3' },
        { name: 'Category III', color: '#3A863D' },
      ],
      zones: [
        {
          name: 'Main Floor',
          position: { x: 0, y: 0 },
          rows: [],
          areas: [],
        },
      ],
      size: { width: 900, height: 900 },
    };
    pushHistory(blank);
    setLastSavedHistoryIndex(historyRef.current.index);
    setSelectedRowIndex(null);
    setSelectedSeatGuids([]);
    setSelectedAreaIndices([]);
    toast({ title: 'New blank plan created', status: 'info', duration: 2000 });
  };

  // Import JSON File
  const handleImportJson = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target.result);
        if (!parsed.zones || !Array.isArray(parsed.zones)) {
          throw new Error('Invalid seating plan JSON: missing zones array.');
        }

        const imported = {
          ...plan,
          name: parsed.name || plan.name || 'Imported Plan',
          categories: parsed.categories || plan.categories || [],
          zones: parsed.zones,
          size: parsed.size || { width: 900, height: 900 },
        };

        // Deduplicate any non-unique seat IDs upon import
        const cleaned = deduplicateSeatGuids(imported);

        pushHistory(cleaned);
        setLastSavedHistoryIndex(historyRef.current.index);
        setActiveZoneIndex(0);
        setSelectedRowIndex(null);
        setSelectedSeatGuids([]);
        setSelectedAreaIndices([]);

        toast({
          title: 'Plan imported successfully',
          description: `Loaded ${cleaned.zones.length} zone(s) safely.`,
          status: 'success',
          duration: 3500,
          isClosable: true,
        });
      } catch (err) {
        toast({
          title: 'Failed to import JSON',
          description: err.message,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Export JSON file
  const handleExportJson = () => {
    if (!plan) return;
    const dataStr =
      'data:text/json;charset=utf-8,' +
      encodeURIComponent(JSON.stringify(plan, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', dataStr);
    downloadAnchor.setAttribute(
      'download',
      `${plan.name || 'seating-plan'}.json`,
    );
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
    toast({ title: 'JSON file downloaded', status: 'success', duration: 2000 });
  };

  // Export PDF (visual vector print)
  const handleExportPdf = () => {
    // Clear selection so handles and gizmos don't appear in print
    setSelectedRowIndex(null);
    setSelectedSeatGuids([]);
    setSelectedAreaIndices([]);

    // Fit and center the entire plan before opening print dialog
    handleCenterView();

    setTimeout(() => {
      window.print();
    }, 150);
  };

  // -------------------------------------------------------------
  // MOVING OBJECTS (Direct Drag on Canvas)
  // -------------------------------------------------------------

  // Grid-snap helper: snaps a value to nearest 10px grid when snap is on
  const snapPos = useCallback(
    (val) => (snapToGrid ? Math.round(val / 10) * 10 : Math.round(val)),
    [snapToGrid],
  );

  // Move Row group: moves row.position and all child seats with it
  const handleMoveRow = useCallback(
    (rowIdx, dx, dy) => {
      setPlan((prev) => {
        if (!prev) return prev;
        const nextZones = prev.zones.map((z, zi) => {
          if (zi !== activeZoneIndex) return z;
          return {
            ...z,
            rows: (z.rows || []).map((r, ri) => {
              if (ri !== rowIdx) return r;
              return {
                ...r,
                position: {
                  x: snapPos((r.position?.x || 0) + dx),
                  y: snapPos((r.position?.y || 0) + dy),
                },
              };
            }),
          };
        });
        return { ...prev, zones: nextZones };
      });
    },
    [activeZoneIndex, snapPos],
  );

  const handleTransformGroup = useCallback(
    (rowIndices, areaIndices, transformData) => {
      setPlan((prev) => {
        if (!prev) return prev;
        const nextZones = prev.zones.map((z, zi) => {
          if (zi !== activeZoneIndex) return z;
          
          if (transformData.scale !== undefined && transformData.cx !== undefined && transformData.cy !== undefined) {
            const s = transformData.scale;
            const cx = transformData.cx;
            const cy = transformData.cy;
            const targetSeatGuids = transformData.seatGuids ? new Set(transformData.seatGuids) : null;
            const initialSeats = transformData.initialSeats || {};
            const initialRows = transformData.initialRows || {};
            const initialAreas = transformData.initialAreas || {};

            const nextRows = (z.rows || []).map((r, ri) => {
              if (!rowIndices.includes(ri)) return r;

              const initRow = initialRows[ri] || {
                x: r.position?.x || 0,
                y: r.position?.y || 0,
                rotation: r.rotation || 0,
                seat_spacing: r.seat_spacing || 25,
                seat_radius: r.seat_radius || 10,
              };
              const rRad = ((initRow.rotation !== undefined ? initRow.rotation : r.rotation) || 0) * (Math.PI / 180);

              let newRowPos = { ...r.position };
              if (initRow) {
                newRowPos.x = Math.round((cx + ((initRow.x || 0) - cx) * s) * 10) / 10;
                newRowPos.y = Math.round((cy + ((initRow.y || 0) - cy) * s) * 10) / 10;
              }

              // Proportionally scale row-level seat_spacing and seat_radius so row properties stay in sync
              const initSpacing = Number(initRow.seat_spacing || r.seat_spacing || 25);
              const newSpacing = Math.round(initSpacing * s * 10) / 10;

              const initRowRadius = Number(initRow.seat_radius || r.seat_radius || 10);
              const newRowRadius = Math.max(4, Math.min(60, Math.round(initRowRadius * s * 10) / 10));

              const nextSeats = (r.seats || []).map((seat) => {
                if (targetSeatGuids && !targetSeatGuids.has(seat.seat_guid)) return seat;

                const initSeat = initialSeats[seat.seat_guid];
                const baseRadius = initSeat ? initSeat.radius : (seat.radius || initRowRadius);
                let newRadius = Math.max(4, Math.min(60, Math.round(baseRadius * s * 10) / 10));
                let newLocalPos = { ...seat.position };

                if (initSeat) {
                  // Scaled absolute position
                  const scaledAbsX = cx + (initSeat.absX - cx) * s;
                  const scaledAbsY = cy + (initSeat.absY - cy) * s;

                  // Transform back to local coords in newRowPos
                  const dx = scaledAbsX - newRowPos.x;
                  const dy = scaledAbsY - newRowPos.y;
                  newLocalPos.x = Math.round((dx * Math.cos(-rRad) - dy * Math.sin(-rRad)) * 10) / 10;
                  newLocalPos.y = Math.round((dx * Math.sin(-rRad) + dy * Math.cos(-rRad)) * 10) / 10;
                }

                return {
                  ...seat,
                  radius: newRadius,
                  position: newLocalPos,
                };
              });

              return {
                ...r,
                position: newRowPos,
                seat_spacing: newSpacing,
                seat_radius: newRowRadius,
                seats: nextSeats,
              };
            });

            const nextAreas = (z.areas || []).map((a, ai) => {
              if (!areaIndices.includes(ai)) return a;
              const initArea = initialAreas[ai];
              if (!initArea) return a;

              const newPos = {
                x: Math.round((cx + ((initArea.x || 0) - cx) * s) * 10) / 10,
                y: Math.round((cy + ((initArea.y || 0) - cy) * s) * 10) / 10,
              };

              let updatedArea = { ...a, position: newPos };
              if (a.rectangle && (initArea.rectWidth || a.rectangle?.width)) {
                const initW = initArea.rectWidth || a.rectangle.width;
                const initH = initArea.rectHeight || a.rectangle.height;
                updatedArea.rectangle = {
                  ...a.rectangle,
                  width: Math.max(10, Math.round(initW * s * 10) / 10),
                  height: Math.max(10, Math.round(initH * s * 10) / 10),
                };
              } else if (a.circle && (initArea.circleRadius || a.circle?.radius)) {
                const initR = initArea.circleRadius || a.circle.radius;
                updatedArea.circle = {
                  ...a.circle,
                  radius: Math.max(5, Math.round(initR * s * 10) / 10),
                };
              } else if (a.ellipse && (initArea.ellipseRadiusX || a.ellipse?.radius?.x)) {
                const initRx = initArea.ellipseRadiusX || a.ellipse?.radius?.x || 50;
                const initRy = initArea.ellipseRadiusY || a.ellipse?.radius?.y || 30;
                updatedArea.ellipse = {
                  ...a.ellipse,
                  radius: {
                    x: Math.max(5, Math.round(initRx * s * 10) / 10),
                    y: Math.max(5, Math.round(initRy * s * 10) / 10),
                  },
                };
              } else if (a.text) {
                const initFont = initArea.fontSize || a.text?.fontSize || 16;
                updatedArea.text = {
                  ...a.text,
                  fontSize: Math.max(8, Math.min(150, Math.round(initFont * s))),
                };
              } else if (a.polygon && initArea.polygonPoints) {
                updatedArea.polygon = {
                  ...a.polygon,
                  points: initArea.polygonPoints.map((p) => ({
                    x: Math.round(p.x * s * 10) / 10,
                    y: Math.round(p.y * s * 10) / 10,
                  })),
                };
              }
              return updatedArea;
            });

            return { ...z, rows: nextRows, areas: nextAreas };
          }

          const nextRows = (z.rows || []).map((r, ri) => {
            if (!rowIndices.includes(ri)) return r;
            
            let newPos = { ...r.position };
            let newRot = r.rotation || 0;
            
            if (transformData.dx !== undefined && transformData.dy !== undefined) {
               newPos.x = Math.round(((r.position?.x || 0) + transformData.dx) * 10) / 10;
               newPos.y = Math.round(((r.position?.y || 0) + transformData.dy) * 10) / 10;
            }
            
            if (transformData.angle !== undefined && transformData.cx !== undefined && transformData.cy !== undefined) {
               const angleRad = transformData.angle * (Math.PI / 180);
               const cos = Math.cos(angleRad);
               const sin = Math.sin(angleRad);
               const x0 = (r.position?.x || 0) - transformData.cx;
               const y0 = (r.position?.y || 0) - transformData.cy;
               
               newPos.x = transformData.cx + (x0 * cos - y0 * sin);
               newPos.y = transformData.cy + (x0 * sin + y0 * cos);
               newRot = newRot + transformData.angle;
            }
            
            return { ...r, position: newPos, rotation: newRot };
          });
          
          const nextAreas = (z.areas || []).map((a, ai) => {
            if (!areaIndices.includes(ai)) return a;
            
            let newPos = { ...a.position };
            let newRot = a.rotation || 0;
            
            if (transformData.dx !== undefined && transformData.dy !== undefined) {
               newPos.x = Math.round(((a.position?.x || 0) + transformData.dx) * 10) / 10;
               newPos.y = Math.round(((a.position?.y || 0) + transformData.dy) * 10) / 10;
            }
            
            if (transformData.angle !== undefined && transformData.cx !== undefined && transformData.cy !== undefined) {
               const angleRad = transformData.angle * (Math.PI / 180);
               const cos = Math.cos(angleRad);
               const sin = Math.sin(angleRad);
               const x0 = (a.position?.x || 0) - transformData.cx;
               const y0 = (a.position?.y || 0) - transformData.cy;
               
               newPos.x = transformData.cx + (x0 * cos - y0 * sin);
               newPos.y = transformData.cy + (x0 * sin + y0 * cos);
               newRot = newRot + transformData.angle;
            }
            
            return { ...a, position: newPos, rotation: newRot };
          });

          return { ...z, rows: nextRows, areas: nextAreas };
        });
        return { ...prev, zones: nextZones };
      });
    },
    [activeZoneIndex, snapPos],
  );

  // Move Single Seat: moves seat.position relative to row.position
  // Preserves its logical membership in its parent row!
  const handleMoveSingleSeat = useCallback(
    (seatGuid, dx, dy) => {
      setPlan((prev) => {
        if (!prev) return prev;
        const nextZones = prev.zones.map((z, zi) => {
          if (zi !== activeZoneIndex) return z;
          return {
            ...z,
            rows: (z.rows || []).map((r) => {
              const seatExists = (r.seats || []).some(
                (s) => s.seat_guid === seatGuid,
              );
              if (!seatExists) return r;
              const rRad = ((r.rotation || 0) * Math.PI) / 180;
              const localDx = dx * Math.cos(-rRad) - dy * Math.sin(-rRad);
              const localDy = dx * Math.sin(-rRad) + dy * Math.cos(-rRad);

              return {
                ...r,
                seats: r.seats.map((s) => {
                  if (s.seat_guid !== seatGuid) return s;
                  return {
                    ...s,
                    position: {
                      x: snapPos((s.position?.x || 0) + localDx),
                      y: snapPos((s.position?.y || 0) + localDy),
                    },
                  };
                }),
              };
            }),
          };
        });
        return { ...prev, zones: nextZones };
      });
    },
    [activeZoneIndex, snapPos],
  );

  const handleMoveMultipleSeats = useCallback(
    (seatGuids, dx, dy) => {
      setPlan((prev) => {
        if (!prev) return prev;
        const guidSet = new Set(seatGuids);
        const nextZones = prev.zones.map((z, zi) => {
          if (zi !== activeZoneIndex) return z;
          
          if (activeTool === 'seat-select') {
            // S mode: move individual seats independently in their respective row local coords
            return {
              ...z,
              rows: (z.rows || []).map((r) => {
                const hasSelectedSeats = (r.seats || []).some((s) =>
                  guidSet.has(s.seat_guid),
                );
                if (!hasSelectedSeats) return r;
                const rRad = ((r.rotation || 0) * Math.PI) / 180;
                const localDx = dx * Math.cos(-rRad) - dy * Math.sin(-rRad);
                const localDy = dx * Math.sin(-rRad) + dy * Math.cos(-rRad);

                return {
                  ...r,
                  seats: r.seats.map((s) => {
                    if (!guidSet.has(s.seat_guid)) return s;
                    return {
                      ...s,
                      position: {
                        x: Math.round(((s.position?.x || 0) + localDx) * 10) / 10,
                        y: Math.round(((s.position?.y || 0) + localDy) * 10) / 10,
                      },
                    };
                  }),
                };
              }),
            };
          } else {
            // V mode: treat row as single element. Move the entire row origin.
            return {
              ...z,
              rows: (z.rows || []).map((r) => {
                const hasSelectedSeats = (r.seats || []).some((s) =>
                  guidSet.has(s.seat_guid),
                );
                if (!hasSelectedSeats) return r;
                return {
                  ...r,
                  position: {
                    x: Math.round(((r.position?.x || 0) + dx) * 10) / 10,
                    y: Math.round(((r.position?.y || 0) + dy) * 10) / 10,
                  },
                };
              }),
            };
          }
        });
        return { ...prev, zones: nextZones };
      });
    },
    [activeZoneIndex, snapPos, activeTool],
  );

  // Move Area / Shape
  const handleMoveArea = useCallback(
    (areaIdx, dx, dy) => {
      setPlan((prev) => {
        if (!prev) return prev;
        const nextZones = prev.zones.map((z, zi) => {
          if (zi !== activeZoneIndex) return z;
          const areas = [...(z.areas || [])];
          if (!areas[areaIdx]) return z;
          areas[areaIdx] = {
            ...areas[areaIdx],
            position: {
              x: snapPos((areas[areaIdx].position?.x || 0) + dx),
              y: snapPos((areas[areaIdx].position?.y || 0) + dy),
            },
          };
          return { ...z, areas };
        });
        return { ...prev, zones: nextZones };
      });
    },
    [activeZoneIndex, snapPos],
  );

  // -------------------------------------------------------------
  // DIRECT INTERACTIVE CREATION HANDLERS
  // -------------------------------------------------------------

  // Add 2D Rows Grid [B]
  const handleCommitCreatedRows = useCallback(
    ({ startX, startY, rowCount, seatsPerRow, seatSpacingX, rowSpacingY }) => {
      if (!plan) return;
      const currentZone = plan.zones[activeZoneIndex] || { rows: [] };
      const startRowNum = (currentZone.rows || []).length + 1;
      const defaultCategory = '';
      const zonePrefix =
        currentZone.id_for_seat_ids || currentZone.name || 'Groundfloor';

      const newRows = [];
      for (let r = 0; r < rowCount; r++) {
        const rowNum = startRowNum + r;
        const seats = [];
        // Each row starts seat numbering from 1
        for (let s = 0; s < seatsPerRow; s++) {
          const seatNum = s + 1;
          const guid = generateUniqueSeatGuid(
            plan,
            zonePrefix,
            rowNum,
            seatNum,
          );
          seats.push({
            seat_number: String(seatNum),
            seat_guid: guid,
            uuid: Math.random().toString(36).substring(2, 9),
            position: { x: s * seatSpacingX, y: 0 },
            category: defaultCategory,
          });
        }
        newRows.push({
          row_number: String(rowNum),
          row_label: String(rowNum),
          show_row_numbers: true,
          row_label_position: 'both',
          category: defaultCategory,
          position: { x: startX, y: startY + r * rowSpacingY },
          seats,
        });
      }

      const updatedZones = [...plan.zones];
      updatedZones[activeZoneIndex] = {
        ...currentZone,
        rows: [...(currentZone.rows || []), ...newRows],
      };

      pushHistory({ ...plan, zones: updatedZones });
      toast({
        title: `Created ${rowCount} rows (${rowCount * seatsPerRow} seats)`,
        status: 'success',
        duration: 2000,
      });
    },
    [plan, activeZoneIndex, pushHistory, toast],
  );

  // Add Angled 1D Row [N]
  const handleCommitCreatedRow = useCallback(
    ({ startX, startY, seatCount, seatSpacing, rotation }) => {
      if (!plan) return;
      const currentZone = plan.zones[activeZoneIndex] || { rows: [] };
      const rowNum = (currentZone.rows || []).length + 1;
      const defaultCategory = '';
      const zonePrefix =
        currentZone.id_for_seat_ids || currentZone.name || 'Groundfloor';

      const seats = [];
      for (let s = 0; s < seatCount; s++) {
        const seatNum = s + 1;
        const guid = generateUniqueSeatGuid(plan, zonePrefix, rowNum, seatNum);
        seats.push({
          seat_number: String(seatNum),
          seat_guid: guid,
          uuid: Math.random().toString(36).substring(2, 9),
          position: { x: s * seatSpacing, y: 0 },
          category: defaultCategory,
        });
      }

      const newRow = {
        row_number: String(rowNum),
        row_label: String(rowNum),
        show_row_numbers: true,
        row_label_position: 'both',
        seat_spacing: seatSpacing,
        rotation: rotation || 0,
        category: defaultCategory,
        position: { x: startX, y: startY },
        seats,
      };

      const updatedZones = [...plan.zones];
      updatedZones[activeZoneIndex] = {
        ...currentZone,
        rows: [...(currentZone.rows || []), newRow],
      };

      pushHistory({ ...plan, zones: updatedZones });
      toast({
        title: `Row ${rowNum} created (${seatCount} seats)`,
        status: 'success',
        duration: 2000,
      });
    },
    [plan, activeZoneIndex, pushHistory, toast],
  );

  // Add Single Seat [M]
  const handleCommitCreatedSeat = useCallback(
    (x, y) => {
      if (!plan) return;
      const currentZone = plan.zones[activeZoneIndex] || { rows: [] };
      const rowNum = (currentZone.rows || []).length + 1;
      const defaultCategory = '';
      const zonePrefix =
        currentZone.id_for_seat_ids || currentZone.name || 'Groundfloor';
      const guid = generateUniqueSeatGuid(plan, zonePrefix, rowNum, 1);

      const newSeat = {
        seat_number: '1',
        seat_guid: guid,
        uuid: Math.random().toString(36).substring(2, 9),
        position: { x: 0, y: 0 },
        category: defaultCategory,
      };

      const newRow = {
        row_number: String(rowNum),
        row_label: "",
        show_row_numbers: true,
        row_label_position: 'both',
        category: defaultCategory,
        position: { x, y },
        seats: [newSeat],
      };

      const updatedZones = [...plan.zones];
      const nextRows = [...(currentZone.rows || []), newRow];
      updatedZones[activeZoneIndex] = {
        ...currentZone,
        rows: nextRows,
      };

      pushHistory({ ...plan, zones: updatedZones });
      setSelectedRowIndex(null);
      setSelectedSeatGuids([guid]);
      setSelectedAreaIndices([]);

      toast({
        title: 'Single seat added',
        status: 'success',
        duration: 1500,
      });
    },
    [plan, activeZoneIndex, pushHistory, toast],
  );

  // Add Area (Rectangle, Circle, Ellipse, Polygon, Text)
  const handleCommitCreatedArea = useCallback(
    (areaData) => {
      if (!plan) return;
      const currentZone = plan.zones[activeZoneIndex] || { areas: [] };
      const nextAreas = [...(currentZone.areas || []), areaData];

      const updatedZones = [...plan.zones];
      updatedZones[activeZoneIndex] = {
        ...currentZone,
        areas: nextAreas,
      };

      pushHistory({ ...plan, zones: updatedZones });
      setSelectedAreaIndices([nextAreas.length - 1]);
      setSelectedRowIndex(null);
      setSelectedSeatGuids([]);

      toast({
        title: `${areaData.shape || 'Shape'} created`,
        status: 'success',
        duration: 1500,
      });
    },
    [plan, activeZoneIndex, pushHistory, toast],
  );

  // -------------------------------------------------------------
  // OBJECT PROPERTIES UPDATE HANDLERS
  // -------------------------------------------------------------

  // Update Row Property
  const handleUpdateRowProperty = useCallback(
    (rowIdx, key, value) => {
      if (!plan) return;
      const currentZone = plan.zones[activeZoneIndex];
      const row = currentZone?.rows?.[rowIdx];
      if (!row) return;

      const updatedRow = {
        ...row,
        [key]: key === 'rotation' ? Number(value) : value,
      };

      // Propagate row settings to child seats
      if (key === 'seat_spacing' || key === 'curve_angle') {
        const parseNum = (v, defaultVal) => {
          if (v === '' || v === '-') return v;
          const num = Number(v);
          return isNaN(num) ? defaultVal : num;
        };
        const spacing =
          key === 'seat_spacing'
            ? parseNum(value, 25)
            : parseNum(row.seat_spacing, 25);
        const curveAngle =
          key === 'curve_angle'
            ? parseNum(value, 0)
            : parseNum(row.curve_angle, 0);
        updatedRow.seat_spacing = spacing;
        updatedRow.curve_angle = curveAngle;
        const positions = calculateRowSeatPositions(
          (row.seats || []).length,
          spacing,
          curveAngle,
          key === 'curve_angle' ? row.seats : null,
        );
        updatedRow.seats = (row.seats || []).map((s, i) => ({
          ...s,
          position: {
            x: positions[i]?.x ?? i * spacing,
            y: positions[i]?.y ?? 0,
          },
          rotation: positions[i]?.rotation ?? 0,
        }));
      } else if (key === 'numbering_pattern') {
        // Regenerate all seat numbers with the new pattern
        const pattern = value;
        const start = row.start_number || 1;
        const isReversed = Boolean(row.reversed);
        const total = (row.seats || []).length;
        updatedRow.seats = (row.seats || []).map((s, i) => ({
          ...s,
          seat_number: generateSeatNum(pattern, start, isReversed, i, total),
        }));
      } else if (key === 'start_number' || key === 'reversed') {
        const pattern = row.numbering_pattern || '1, 2, 3, …';
        const start = key === 'start_number' ? value : row.start_number || 1;
        const isReversed =
          key === 'reversed' ? Boolean(value) : Boolean(row.reversed);
        const total = (row.seats || []).length;

        updatedRow.seats = (row.seats || []).map((s, i) => ({
          ...s,
          seat_number: generateSeatNum(pattern, start, isReversed, i, total),
        }));
      } else if (key === 'category') {
        updatedRow.category = value;
        updatedRow.seats = (row.seats || []).map((s) => ({
          ...s,
          category: value,
        }));
      } else if (key === 'seat_radius') {
        updatedRow.seat_radius = Number(value);
        updatedRow.seats = (row.seats || []).map((s) => ({
          ...s,
          radius: Number(value),
        }));
      } else if (key === 'seat_label') {
        updatedRow.seat_label = value;
      } else if (key === 'row_number' || key === 'row_label') {
        const rawPrefix =
          currentZone.id_for_seat_ids || currentZone.name || 'MainFloor';
        const zonePrefix = (rawPrefix === 'Groundfloor' ? 'MainFloor' : rawPrefix).replace(/\s+/g, '');
        const effectiveRow =
          key === 'row_label'
            ? value && String(value).trim() !== ''
              ? String(value).trim()
              : String(row.row_number || '')
            : row.row_label && String(row.row_label).trim() !== ''
            ? String(row.row_label).trim()
            : String(value || '');

        updatedRow.seats = (row.seats || []).map((s) => ({
          ...s,
          seat_guid: `${zonePrefix}-${effectiveRow}-${s.seat_number}`,
        }));
      }

      const updatedZones = [...plan.zones];
      const nextRows = [...currentZone.rows];
      nextRows[rowIdx] = updatedRow;
      updatedZones[activeZoneIndex] = { ...currentZone, rows: nextRows };

      pushHistory({ ...plan, zones: updatedZones });
    },
    [plan, activeZoneIndex, pushHistory],
  );

  // Add a seat at the end of a row
  const handleAddSeatToRow = useCallback(
    (rowIdx) => {
      if (!plan) return;
      const currentZone = plan.zones[activeZoneIndex];
      const row = currentZone?.rows?.[rowIdx];
      if (!row) return;

      const currentSeats = row.seats || [];
      const newCount = currentSeats.length + 1;
      const spacing = Number(row.seat_spacing) || 25;
      const curveAngle = Number(row.curve_angle) || 0;
      const zonePrefix = (
        currentZone.id_for_seat_ids ||
        currentZone.name ||
        'Groundfloor'
      ).replace(/\s+/g, '');
      const pattern = row.numbering_pattern || '1, 2, 3, …';
      const start = row.start_number || 1;
      const isReversed = Boolean(row.reversed);
      const defaultCategory = '';
      const radius = row.seat_radius || currentSeats[0]?.radius || 10;

      const positions = calculateRowSeatPositions(
        newCount,
        spacing,
        curveAngle,
        currentSeats,
      );
      const newSeatNum = generateSeatNum(
        pattern,
        start,
        isReversed,
        newCount - 1,
        newCount,
      );
      const newGuid = generateUniqueSeatGuid(
        plan,
        zonePrefix,
        row.row_number,
        newSeatNum,
      );

      const newSeat = {
        seat_number: newSeatNum,
        seat_guid: newGuid,
        uuid: Math.random().toString(36).substring(2, 9),
        position: {
          x: positions[newCount - 1].x,
          y: positions[newCount - 1].y,
        },
        rotation: positions[newCount - 1].rotation,
        radius,
        category: defaultCategory,
      };

      const updatedSeats = [...currentSeats, newSeat].map((s, i) => ({
        ...s,
        seat_number: generateSeatNum(pattern, start, isReversed, i, newCount),
        position: {
          x: positions[i].x,
          y: positions[i].y,
        },
        rotation: positions[i].rotation,
      }));

      const updatedRow = { ...row, seats: updatedSeats };
      const updatedZones = [...plan.zones];
      const nextRows = [...currentZone.rows];
      nextRows[rowIdx] = updatedRow;
      updatedZones[activeZoneIndex] = { ...currentZone, rows: nextRows };

      pushHistory({ ...plan, zones: updatedZones });
      toast({
        title: `Seat added to Row ${row.row_number}`,
        status: 'success',
        duration: 1200,
      });
    },
    [plan, activeZoneIndex, pushHistory, toast],
  );

  // Remove the last seat from a row
  const handleRemoveSeatFromRow = useCallback(
    (rowIdx) => {
      if (!plan) return;
      const currentZone = plan.zones[activeZoneIndex];
      const row = currentZone?.rows?.[rowIdx];
      if (!row || !row.seats || row.seats.length <= 1) return;

      const currentSeats = row.seats;
      const newCount = currentSeats.length - 1;
      const spacing = Number(row.seat_spacing) || 25;
      const curveAngle = Number(row.curve_angle) || 0;
      const pattern = row.numbering_pattern || '1, 2, 3, …';
      const start = row.start_number || 1;
      const isReversed = Boolean(row.reversed);

      const positions = calculateRowSeatPositions(
        newCount,
        spacing,
        curveAngle,
        currentSeats,
      );

      const updatedSeats = currentSeats.slice(0, newCount).map((s, i) => ({
        ...s,
        seat_number: generateSeatNum(pattern, start, isReversed, i, newCount),
        position: {
          x: positions[i].x,
          y: positions[i].y,
        },
        rotation: positions[i].rotation,
      }));

      const updatedRow = { ...row, seats: updatedSeats };
      const updatedZones = [...plan.zones];
      const nextRows = [...currentZone.rows];
      nextRows[rowIdx] = updatedRow;
      updatedZones[activeZoneIndex] = { ...currentZone, rows: nextRows };

      pushHistory({ ...plan, zones: updatedZones });
      toast({
        title: `Seat removed from Row ${row.row_number}`,
        status: 'info',
        duration: 1200,
      });
    },
    [plan, activeZoneIndex, pushHistory, toast],
  );

  // Update Single Seat Property
  const handleUpdateSingleSeatProperty = useCallback(
    (seatGuid, key, value) => {
      if (!plan) return;
      const currentZone = plan.zones[activeZoneIndex];
      if (!currentZone) return;

      const rawPrefix =
        currentZone.id_for_seat_ids || currentZone.name || 'MainFloor';
      const zonePrefix = (rawPrefix === 'Groundfloor' ? 'MainFloor' : rawPrefix).replace(/\s+/g, '');

      let newSeatGuid = seatGuid;

      const nextRows = (currentZone.rows || []).map((r) => {
        const seatExists = (r.seats || []).some(
          (s) => s.seat_guid === seatGuid,
        );
        if (!seatExists) return r;

        const effectiveRow =
          r.row_label && String(r.row_label).trim() !== ''
            ? String(r.row_label).trim()
            : String(r.row_number || '');

        return {
          ...r,
          seats: r.seats.map((s) => {
            if (s.seat_guid !== seatGuid) return s;
            const updatedSeat = {
              ...s,
              [key]:
                key === 'radius' || key === 'rotation' ? Number(value) : value,
            };
            if (key === 'seat_number') {
              updatedSeat.seat_guid = `${zonePrefix}-${effectiveRow}-${value}`;
              newSeatGuid = updatedSeat.seat_guid;
            }
            return updatedSeat;
          }),
        };
      });

      const updatedZones = [...plan.zones];
      updatedZones[activeZoneIndex] = { ...currentZone, rows: nextRows };

      // If seat_guid was changed or seat_number changed, update selection state
      if (key === 'seat_guid') {
        setSelectedSeatGuids([value]);
      } else if (key === 'seat_number' && newSeatGuid !== seatGuid) {
        setSelectedSeatGuids([newSeatGuid]);
      }

      pushHistory({ ...plan, zones: updatedZones });
    },
    [plan, activeZoneIndex, pushHistory],
  );

  // Batch update properties across multiple selected seats
  const handleBatchUpdateSeats = useCallback(
    (guids, updates) => {
      if (!plan || !guids || guids.length === 0) return;
      const currentZone = plan.zones[activeZoneIndex];
      if (!currentZone) return;

      const guidSet = new Set(guids);
      const nextRows = (currentZone.rows || []).map((r) => ({
        ...r,
        seats: (r.seats || []).map((s) => {
          if (!guidSet.has(s.seat_guid)) return s;
          return {
            ...s,
            ...updates,
          };
        }),
      }));

      const updatedZones = [...plan.zones];
      updatedZones[activeZoneIndex] = { ...currentZone, rows: nextRows };
      pushHistory({ ...plan, zones: updatedZones });
    },
    [plan, activeZoneIndex, pushHistory],
  );

  // Update Area
  const handleUpdateArea = useCallback(
    (idx, areaData) => {
      if (!plan || idx === null) return;
      const currentZone = plan.zones[activeZoneIndex];
      const areas = [...(currentZone.areas || [])];
      areas[idx] = areaData;

      const updatedZones = [...plan.zones];
      updatedZones[activeZoneIndex] = { ...currentZone, areas };

      pushHistory({ ...plan, zones: updatedZones });
    },
    [plan, activeZoneIndex, pushHistory],
  );

  // Delete Area
  const handleDeleteArea = useCallback(
    (idx) => {
      if (!plan || idx === null) return;
      const currentZone = plan.zones[activeZoneIndex];
      const areas = (currentZone.areas || []).filter((_, i) => i !== idx);

      const updatedZones = [...plan.zones];
      updatedZones[activeZoneIndex] = { ...currentZone, areas };

      pushHistory({ ...plan, zones: updatedZones });
      setSelectedAreaIndices([]);
    },
    [plan, activeZoneIndex, pushHistory],
  );

  // Reorder Area Layer (Stacking Order: Front, Back, Forward, Backward)
  const handleReorderAreaLayer = useCallback(
    (idx, direction) => {
      if (!plan || idx === null) return;
      const currentZone = plan.zones[activeZoneIndex];
      const areas = [...(currentZone.areas || [])];
      const target = areas[idx];
      if (!target) return;

      let newIdx = idx;
      if (direction === 'front') {
        areas.splice(idx, 1);
        areas.push(target);
        newIdx = areas.length - 1;
      } else if (direction === 'back') {
        areas.splice(idx, 1);
        areas.unshift(target);
        newIdx = 0;
      } else if (direction === 'forward' && idx < areas.length - 1) {
        areas[idx] = areas[idx + 1];
        areas[idx + 1] = target;
        newIdx = idx + 1;
      } else if (direction === 'backward' && idx > 0) {
        areas[idx] = areas[idx - 1];
        areas[idx - 1] = target;
        newIdx = idx - 1;
      }

      const updatedZones = [...plan.zones];
      updatedZones[activeZoneIndex] = { ...currentZone, areas };

      pushHistory({ ...plan, zones: updatedZones });
      setSelectedAreaIndices([newIdx]);
    },
    [plan, activeZoneIndex, pushHistory],
  );

  // -------------------------------------------------------------
  // CLIPBOARD OPERATIONS (Cut, Copy, Paste, Delete)
  // -------------------------------------------------------------

  // Delete Selection
  const handleDeleteSelection = useCallback(() => {
    if (!plan) return;
    const currentZone = plan.zones[activeZoneIndex];
    let nextAreas = [...(currentZone.areas || [])];
    let nextRows = [...(currentZone.rows || [])];
    let modified = false;

    if (selectedAreaIndices.length > 0) {
      const sortedIndices = [...selectedAreaIndices].sort((a, b) => b - a);
      sortedIndices.forEach((idx) => {
        nextAreas.splice(idx, 1);
      });
      modified = true;
    }

    if (selectedRowIndex !== null && selectedSeatGuids.length === 0) {
      // If only row is selected (but no specific seats), delete the whole row
      nextRows = nextRows.filter((_, i) => i !== selectedRowIndex);
      modified = true;
    } else if (selectedSeatGuids.length > 0) {
      nextRows = nextRows
        .map((row) => ({
          ...row,
          seats: (row.seats || []).filter(
            (s) => !selectedSeatGuids.includes(s.seat_guid),
          ),
        }))
        .filter((row) => (row.seats || []).length > 0);
      modified = true;
    }

    if (modified) {
      const updatedZones = [...plan.zones];
      updatedZones[activeZoneIndex] = {
        ...currentZone,
        areas: nextAreas,
        rows: nextRows,
      };

      pushHistory({ ...plan, zones: updatedZones });
      setSelectedAreaIndices([]);
      setSelectedRowIndex(null);
      setSelectedSeatGuids([]);
      toast({
        title: 'Selection deleted',
        status: 'info',
        duration: 1500,
      });
    }
  }, [
    plan,
    activeZoneIndex,
    selectedAreaIndices,
    selectedRowIndex,
    selectedSeatGuids,
    pushHistory,
    toast,
  ]);

  const handleAlign = useCallback(
    (alignmentType) => {
      if (!plan) return;
      const currentZone = plan.zones[activeZoneIndex];
      const hasAreas = selectedAreaIndices.length > 0;
      const hasSeats = selectedSeatGuids.length > 0;
      const hasRow = selectedRowIndex !== null;

      if (!hasAreas && !hasSeats && !hasRow) return;

      const items = [];

      // 1. Gather bounding boxes for selected Areas
      if (hasAreas) {
        selectedAreaIndices.forEach((idx) => {
          const area = currentZone.areas[idx];
          if (area) {
             let minX = area.position?.x || 0;
             let minY = area.position?.y || 0;
             let maxX = minX;
             let maxY = minY;
             
             if (area.shape === 'circle') {
               const r = area.circle?.radius || 0;
               minX -= r; maxX += r; minY -= r; maxY += r;
             } else if (area.shape === 'ellipse') {
               const rx = area.ellipse?.radius?.x || 0;
               const ry = area.ellipse?.radius?.y || 0;
               minX -= rx; maxX += rx; minY -= ry; maxY += ry;
             } else if (area.shape === 'rectangle') {
               const w = area.rectangle?.width || 0;
               const h = area.rectangle?.height || 0;
               maxX += w; maxY += h;
             }
             const centerX = (minX + maxX) / 2;
             const centerY = (minY + maxY) / 2;
             items.push({ type: 'area', idx, bbox: { minX, maxX, minY, maxY, centerX, centerY } });
          }
        });
      }

      // 2. Gather bounding boxes for selected Rows/Seats
      if (hasRow && !hasSeats) {
        const row = currentZone.rows[selectedRowIndex];
        if (row && row.seats && row.seats.length > 0) {
           const xs = row.seats.map(s => (row.position?.x || 0) + (s.position?.x || 0));
           const ys = row.seats.map(s => (row.position?.y || 0) + (s.position?.y || 0));
           const minX = Math.min(...xs);
           const maxX = Math.max(...xs);
           const minY = Math.min(...ys);
           const maxY = Math.max(...ys);
           items.push({ 
             type: 'row', idx: selectedRowIndex, 
             bbox: { minX, maxX, minY, maxY, centerX: (minX + maxX)/2, centerY: (minY + maxY)/2 }
           });
        }
      } else if (hasSeats) {
        const selectedSet = new Set(selectedSeatGuids);
        if (activeTool === 'seat-select') {
          // S Mode: align individual seats
          (currentZone.rows || []).forEach((row, rIdx) => {
            (row.seats || []).forEach((s) => {
              if (selectedSet.has(s.seat_guid)) {
                const absX = (row.position?.x || 0) + (s.position?.x || 0);
                const absY = (row.position?.y || 0) + (s.position?.y || 0);
                items.push({
                  type: 'seat', rIdx, seat_guid: s.seat_guid,
                  bbox: { minX: absX, maxX: absX, minY: absY, maxY: absY, centerX: absX, centerY: absY }
                });
              }
            });
          });
        } else {
          // V Mode: align the entire row based on the visual bounding box of all its seats
          (currentZone.rows || []).forEach((row, rIdx) => {
            const hasSelectedSeat = (row.seats || []).some(s => selectedSet.has(s.seat_guid));
            if (hasSelectedSeat && row.seats && row.seats.length > 0) {
               const xs = row.seats.map(s => (row.position?.x || 0) + (s.position?.x || 0));
               const ys = row.seats.map(s => (row.position?.y || 0) + (s.position?.y || 0));
               const minX = Math.min(...xs);
               const maxX = Math.max(...xs);
               const minY = Math.min(...ys);
               const maxY = Math.max(...ys);
               items.push({
                 type: 'row', idx: rIdx,
                 bbox: { minX, maxX, minY, maxY, centerX: (minX + maxX)/2, centerY: (minY + maxY)/2 }
               });
            }
          });
        }
      }

      if (items.length < 2) {
        toast({
          title: 'Select at least 2 items to align',
          status: 'warning',
          duration: 1500,
        });
        return;
      }

      if (alignmentType === 'distribute-h' || alignmentType === 'distribute-v') {
        if (items.length < 3) {
          toast({
            title: 'Select at least 3 items to distribute evenly',
            status: 'warning',
            duration: 2000,
          });
          return;
        }

        let nextAreas = [...(currentZone.areas || [])];
        let nextRows = [...(currentZone.rows || [])].map(r => ({ ...r, seats: [...(r.seats || [])] }));

        if (alignmentType === 'distribute-h') {
          // Sort items by centerX
          const sorted = [...items].sort((a, b) => a.bbox.centerX - b.bbox.centerX);
          const firstX = sorted[0].bbox.centerX;
          const lastX = sorted[sorted.length - 1].bbox.centerX;
          const step = (lastX - firstX) / (sorted.length - 1);

          sorted.forEach((item, idx) => {
            if (idx === 0 || idx === sorted.length - 1) return; // Keep endpoints fixed
            const targetX = firstX + idx * step;
            const deltaX = Math.round((targetX - item.bbox.centerX) * 10) / 10;

            if (item.type === 'area') {
              nextAreas[item.idx] = {
                ...nextAreas[item.idx],
                position: {
                  x: Math.round(((nextAreas[item.idx].position?.x || 0) + deltaX) * 10) / 10,
                  y: nextAreas[item.idx].position?.y || 0,
                }
              };
            } else if (item.type === 'row') {
              nextRows[item.idx] = {
                ...nextRows[item.idx],
                position: {
                  x: Math.round(((nextRows[item.idx].position?.x || 0) + deltaX) * 10) / 10,
                  y: nextRows[item.idx].position?.y || 0,
                }
              };
            } else if (item.type === 'seat') {
              const row = nextRows[item.rIdx];
              const seatIdx = row.seats.findIndex(s => s.seat_guid === item.seat_guid);
              if (seatIdx !== -1) {
                const s = row.seats[seatIdx];
                row.seats[seatIdx] = {
                  ...s,
                  position: {
                    x: Math.round(((s.position?.x || 0) + deltaX) * 10) / 10,
                    y: s.position?.y || 0,
                  }
                };
              }
            }
          });
        } else if (alignmentType === 'distribute-v') {
          // Sort items by centerY
          const sorted = [...items].sort((a, b) => a.bbox.centerY - b.bbox.centerY);
          const firstY = sorted[0].bbox.centerY;
          const lastY = sorted[sorted.length - 1].bbox.centerY;
          const step = (lastY - firstY) / (sorted.length - 1);

          sorted.forEach((item, idx) => {
            if (idx === 0 || idx === sorted.length - 1) return; // Keep endpoints fixed
            const targetY = firstY + idx * step;
            const deltaY = Math.round((targetY - item.bbox.centerY) * 10) / 10;

            if (item.type === 'area') {
              nextAreas[item.idx] = {
                ...nextAreas[item.idx],
                position: {
                  x: nextAreas[item.idx].position?.x || 0,
                  y: Math.round(((nextAreas[item.idx].position?.y || 0) + deltaY) * 10) / 10,
                }
              };
            } else if (item.type === 'row') {
              nextRows[item.idx] = {
                ...nextRows[item.idx],
                position: {
                  x: nextRows[item.idx].position?.x || 0,
                  y: Math.round(((nextRows[item.idx].position?.y || 0) + deltaY) * 10) / 10,
                }
              };
            } else if (item.type === 'seat') {
              const row = nextRows[item.rIdx];
              const seatIdx = row.seats.findIndex(s => s.seat_guid === item.seat_guid);
              if (seatIdx !== -1) {
                const s = row.seats[seatIdx];
                row.seats[seatIdx] = {
                  ...s,
                  position: {
                    x: s.position?.x || 0,
                    y: Math.round(((s.position?.y || 0) + deltaY) * 10) / 10,
                  }
                };
              }
            }
          });
        }

        const updatedZones = [...plan.zones];
        updatedZones[activeZoneIndex] = {
          ...currentZone,
          areas: nextAreas,
          rows: nextRows,
        };

        pushHistory({ ...plan, zones: updatedZones });
        toast({
          title: alignmentType === 'distribute-h' ? 'Distributed Horizontally' : 'Distributed Vertically',
          status: 'success',
          duration: 1000,
        });
        return;
      }

      // 3. Find global bounding box of all selected items
      const allMinX = Math.min(...items.map(i => i.bbox.minX));
      const allMaxX = Math.max(...items.map(i => i.bbox.maxX));
      const allMinY = Math.min(...items.map(i => i.bbox.minY));
      const allMaxY = Math.max(...items.map(i => i.bbox.maxY));
      const allCenterX = (allMinX + allMaxX) / 2;
      const allCenterY = (allMinY + allMaxY) / 2;

      let targetX = null;
      let targetY = null;
      let alignAxis = null;

      switch (alignmentType) {
        case 'top': targetY = allMinY; alignAxis = 'y'; break;
        case 'middle': targetY = allCenterY; alignAxis = 'y'; break;
        case 'bottom': targetY = allMaxY; alignAxis = 'y'; break;
        case 'left': targetX = allMinX; alignAxis = 'x'; break;
        case 'center': targetX = allCenterX; alignAxis = 'x'; break;
        case 'right': targetX = allMaxX; alignAxis = 'x'; break;
        default: return;
      }

      let nextAreas = [...(currentZone.areas || [])];
      let nextRows = [...(currentZone.rows || [])].map(r => ({ ...r, seats: [...(r.seats || [])] }));

      items.forEach((item) => {
        let deltaX = 0;
        let deltaY = 0;
        
        if (alignAxis === 'x') {
          if (alignmentType === 'left') deltaX = targetX - item.bbox.minX;
          else if (alignmentType === 'center') deltaX = targetX - item.bbox.centerX;
          else if (alignmentType === 'right') deltaX = targetX - item.bbox.maxX;
        } else if (alignAxis === 'y') {
          if (alignmentType === 'top') deltaY = targetY - item.bbox.minY;
          else if (alignmentType === 'middle') deltaY = targetY - item.bbox.centerY;
          else if (alignmentType === 'bottom') deltaY = targetY - item.bbox.maxY;
        }

        if (item.type === 'area') {
          nextAreas[item.idx] = {
            ...nextAreas[item.idx],
            position: {
              x: (nextAreas[item.idx].position?.x || 0) + deltaX,
              y: (nextAreas[item.idx].position?.y || 0) + deltaY,
            }
          };
        } else if (item.type === 'row') {
          nextRows[item.idx] = {
            ...nextRows[item.idx],
            position: {
              x: (nextRows[item.idx].position?.x || 0) + deltaX,
              y: (nextRows[item.idx].position?.y || 0) + deltaY,
            }
          };
        } else if (item.type === 'seat') {
          const row = nextRows[item.rIdx];
          const seatIdx = row.seats.findIndex(s => s.seat_guid === item.seat_guid);
          if (seatIdx !== -1) {
            const s = row.seats[seatIdx];
            row.seats[seatIdx] = {
              ...s,
              position: {
                x: (s.position?.x || 0) + deltaX,
                y: (s.position?.y || 0) + deltaY,
              }
            };
          }
        }
      });

      const updatedZones = [...plan.zones];
      updatedZones[activeZoneIndex] = {
        ...currentZone,
        areas: nextAreas,
        rows: nextRows,
      };

      pushHistory({ ...plan, zones: updatedZones });
      toast({
        title: `Aligned ${alignmentType}`,
        status: 'success',
        duration: 1000,
      });
    },
    [plan, activeZoneIndex, selectedAreaIndices, selectedSeatGuids, selectedRowIndex, activeTool, pushHistory, toast],
  );

  const handleCopy = useCallback(() => {
    if (!plan) return;

    const currentZone = plan.zones[activeZoneIndex];
    const hasAreas = selectedAreaIndices.length > 0;
    const hasSeats = selectedSeatGuids.length > 0;
    const hasRow = selectedRowIndex !== null;

    // Mixed selection: areas + seats
    if (hasAreas && hasSeats) {
      const areas = selectedAreaIndices
        .map((idx) => currentZone?.areas?.[idx])
        .filter(Boolean);
      const selectedSet = new Set(selectedSeatGuids);
      const copiedSeats = [];

      (currentZone?.rows || []).forEach((row) => {
        (row.seats || []).forEach((s) => {
          if (selectedSet.has(s.seat_guid)) {
            const copiedSeat = JSON.parse(JSON.stringify(s));
            copiedSeat.absPos = {
              x: (row.position?.x || 0) + (s.position?.x || 0),
              y: (row.position?.y || 0) + (s.position?.y || 0),
            };
            copiedSeats.push(copiedSeat);
          }
        });
      });

      if (areas.length > 0 || copiedSeats.length > 0) {
        setClipboard({
          type: 'mixed',
          data: {
            areas: JSON.parse(JSON.stringify(areas)),
            seats: copiedSeats,
          },
        });
        toast({
          title: `${areas.length} shape(s) and ${copiedSeats.length} seat(s) copied`,
          status: 'info',
          duration: 1200,
        });
      }
      return;
    }

    // Only areas selected
    if (hasAreas) {
      const areas = selectedAreaIndices
        .map((idx) => currentZone?.areas?.[idx])
        .filter(Boolean);
      if (areas.length > 0) {
        setClipboard({
          type: 'areas',
          data: JSON.parse(JSON.stringify(areas)),
        });
        toast({
          title: `${areas.length} shape(s) copied to clipboard`,
          status: 'info',
          duration: 1200,
        });
      }
      return;
    }

    // Row selected
    if (hasRow) {
      const row = currentZone?.rows?.[selectedRowIndex];
      if (row) {
        setClipboard({ type: 'row', data: JSON.parse(JSON.stringify(row)) });
        toast({
          title: `Row ${row.row_number} copied`,
          status: 'info',
          duration: 1200,
        });
      }
      return;
    }

    // Only seats selected
    if (hasSeats) {
      const selectedSet = new Set(selectedSeatGuids);
      const copiedSeats = [];

      (currentZone?.rows || []).forEach((row) => {
        (row.seats || []).forEach((s) => {
          if (selectedSet.has(s.seat_guid)) {
            const copiedSeat = JSON.parse(JSON.stringify(s));
            copiedSeat.absPos = {
              x: (row.position?.x || 0) + (s.position?.x || 0),
              y: (row.position?.y || 0) + (s.position?.y || 0),
            };
            copiedSeats.push(copiedSeat);
          }
        });
      });

      if (copiedSeats.length > 0) {
        setClipboard({ type: 'seats', data: copiedSeats });
        toast({
          title: `${copiedSeats.length} seat(s) copied`,
          status: 'info',
          duration: 1200,
        });
      }
    }
  }, [
    plan,
    activeZoneIndex,
    selectedAreaIndices,
    selectedRowIndex,
    selectedSeatGuids,
    toast,
  ]);

  // Cut Selection
  const handleCut = useCallback(() => {
    handleCopy();
    handleDeleteSelection();
  }, [handleCopy, handleDeleteSelection]);

  // Paste Selection (with Guaranteed Unique Seat IDs)
  const handlePaste = useCallback(() => {
    if (!clipboard || !plan) return;
    const currentZone = plan.zones[activeZoneIndex];
    const zonePrefix =
      currentZone?.id_for_seat_ids || currentZone?.name || 'Groundfloor';

    // Mixed selection: areas + seats
    if (clipboard.type === 'mixed') {
      const { areas, seats } = clipboard.data;
      const offsetX = 30;
      const offsetY = 30;

      // Paste areas with offset
      const newAreas = areas.map((area) => ({
        ...area,
        uuid: Math.random().toString(36).substring(2, 9),
        position: {
          x: (area.position?.x || 0) + offsetX,
          y: (area.position?.y || 0) + offsetY,
        },
      }));

      // Paste seats with offset to maintain relative positions
      const newSeatGuids = [];
      const updatedRows = [...(currentZone.rows || [])];

      if (seats.length > 0) {
        // Find the original row position for the seats
        // For now, we'll create a new row with offset position
        const nextRowNum = updatedRows.length + 1;
        const newSeats = seats.map((s, idx) => {
          const seatNum = idx + 1;
          const newSeat = {
            ...s,
            uuid: Math.random().toString(36).substring(2, 9),
            seat_number: String(seatNum),
            seat_guid: generateUniqueSeatGuid(
              plan,
              zonePrefix,
              nextRowNum,
              seatNum,
            ),
            position: {
              x: (s.absPos?.x || s.position?.x || 0) + offsetX,
              y: (s.absPos?.y || s.position?.y || 0) + offsetY,
            },
          };
          newSeatGuids.push(newSeat.seat_guid);
          return newSeat;
        });

        const newRow = {
          row_number: String(nextRowNum),
          row_label: String(nextRowNum),
          show_row_numbers: true,
          row_label_position: 'both',
          category: '',
          position: { x: 0, y: 0 },
          seats: newSeats,
        };

        updatedRows.push(newRow);
      }

      // Apply changes
      const nextAreas = [...(currentZone.areas || []), ...newAreas];
      const updatedZones = [...plan.zones];
      updatedZones[activeZoneIndex] = {
        ...currentZone,
        areas: nextAreas,
        rows: updatedRows,
      };

      pushHistory({ ...plan, zones: updatedZones });

      // Select the newly pasted items
      const newAreaIndices = newAreas.map(
        (_, i) => nextAreas.length - newAreas.length + i,
      );
      setSelectedAreaIndices(newAreaIndices);
      setSelectedSeatGuids(newSeatGuids);
      setSelectedRowIndex(null);

      toast({
        title: `${newAreas.length} shape(s) and ${seats.length} seat(s) pasted`,
        status: 'success',
        duration: 1500,
      });
      return;
    }

    if (clipboard.type === 'areas') {
      const newAreas = clipboard.data.map((area) => ({
        ...area,
        uuid: Math.random().toString(36).substring(2, 9),
        position: {
          x: (area.position?.x || 0) + 30,
          y: (area.position?.y || 0) + 30,
        },
      }));
      const nextAreas = [...(currentZone.areas || []), ...newAreas];
      const updatedZones = [...plan.zones];
      updatedZones[activeZoneIndex] = {
        ...currentZone,
        areas: nextAreas,
      };
      pushHistory({ ...plan, zones: updatedZones });
      const newIndices = newAreas.map(
        (_, i) => nextAreas.length - newAreas.length + i,
      );
      setSelectedAreaIndices(newIndices);
      toast({
        title: `${newAreas.length} shape(s) pasted`,
        status: 'success',
        duration: 1500,
      });
      return;
    }

    if (clipboard.type === 'area') {
      const newArea = {
        ...clipboard.data,
        uuid: Math.random().toString(36).substring(2, 9),
        position: {
          x: (clipboard.data.position?.x || 0) + 30,
          y: (clipboard.data.position?.y || 0) + 30,
        },
      };
      handleCommitCreatedArea(newArea);
      toast({ title: 'Shape pasted', status: 'success', duration: 1500 });
      return;
    }

    if (clipboard.type === 'row') {
      const baseRow = clipboard.data;
      const nextRowNum = (currentZone.rows || []).length + 1;
      const newSeats = (baseRow.seats || []).map((s, idx) => {
        const seatNum = idx + 1;
        return {
          ...s,
          uuid: Math.random().toString(36).substring(2, 9),
          seat_number: String(seatNum),
          seat_guid: generateUniqueSeatGuid(
            plan,
            zonePrefix,
            nextRowNum,
            seatNum,
          ),
        };
      });

      const newRow = {
        ...baseRow,
        row_number: String(nextRowNum),
        row_label: String(nextRowNum),
        row_label_position: baseRow.row_label_position || 'both',
        position: {
          x: (baseRow.position?.x || 0) + 25,
          y: (baseRow.position?.y || 0) + 35,
        },
        seats: newSeats,
      };

      const updatedZones = [...plan.zones];
      const newRowList = [...(currentZone.rows || []), newRow];
      updatedZones[activeZoneIndex] = {
        ...currentZone,
        rows: newRowList,
      };

      pushHistory({ ...plan, zones: updatedZones });
      // Set selection to the newly pasted row (last index)
      setSelectedRowIndex(newRowList.length - 1);
      setSelectedSeatGuids([]);
      setSelectedAreaIndices([]);
      toast({
        title: 'Row pasted with new unique IDs',
        status: 'success',
        duration: 1500,
      });
      return;
    }

    if (clipboard.type === 'seats') {
      const nextRowNum = (currentZone.rows || []).length + 1;
      const offsetX = 30;
      const offsetY = 30;

      // Find the minimum position to use as reference for maintaining relative positions
      const minX = Math.min(...clipboard.data.map((s) => s.absPos?.x ?? s.position?.x ?? 0));
      const minY = Math.min(...clipboard.data.map((s) => s.absPos?.y ?? s.position?.y ?? 0));

      const newSeats = clipboard.data.map((s, idx) => {
        const seatNum = idx + 1;
        return {
          ...s,
          uuid: Math.random().toString(36).substring(2, 9),
          seat_number: String(seatNum),
          seat_guid: generateUniqueSeatGuid(
            plan,
            zonePrefix,
            nextRowNum,
            seatNum,
          ),
          position: {
            x: (s.absPos?.x ?? s.position?.x ?? 0) - minX + offsetX,
            y: (s.absPos?.y ?? s.position?.y ?? 0) - minY + offsetY,
          },
        };
      });

      const newRow = {
        row_number: String(nextRowNum),
        row_label: String(nextRowNum),
        show_row_numbers: true,
        row_label_position: 'both',
        position: { x: minX, y: minY },
        seats: newSeats,
      };

      const updatedZones = [...plan.zones];
      updatedZones[activeZoneIndex] = {
        ...currentZone,
        rows: [...(currentZone.rows || []), newRow],
      };

      pushHistory({ ...plan, zones: updatedZones });
      setSelectedRowIndex(null);
      setSelectedSeatGuids(newSeats.map((s) => s.seat_guid));
      setSelectedAreaIndices([]);
      toast({
        title: 'Seats pasted with new unique IDs',
        status: 'success',
        duration: 1500,
      });
    }
  }, [
    clipboard,
    plan,
    activeZoneIndex,
    handleCommitCreatedArea,
    pushHistory,
    toast,
  ]);

  // -------------------------------------------------------------
  // ZONE MANAGEMENT HANDLERS
  // -------------------------------------------------------------

  const handleAddZone = useCallback(
    (name) => {
      if (!plan) return;
      const nextZones = [
        ...(plan.zones || []),
        {
          name,
          id_for_seat_ids: name.replace(/\s+/g, ''),
          position: { x: 0, y: 0 },
          rows: [],
          areas: [],
        },
      ];
      pushHistory({ ...plan, zones: nextZones });
      setActiveZoneIndex(nextZones.length - 1);
      toast({
        title: `Zone "${name}" created`,
        status: 'success',
        duration: 2000,
      });
    },
    [plan, pushHistory, toast],
  );

  const handleEditZone = useCallback(
    (zIdx, data) => {
      if (!plan || !plan.zones[zIdx]) return;
      const nextZones = [...plan.zones];
      const targetZone = nextZones[zIdx];
      const newPrefix = (data.id_for_seat_ids || data.name || 'MainFloor').replace(/\s+/g, '');
      const oldPrefix = (targetZone.id_for_seat_ids || targetZone.name || 'Groundfloor').replace(/\s+/g, '');

      // Synchronize all seat_guids in this zone with the new zone prefix
      const updatedRows = (targetZone.rows || []).map((row) => ({
        ...row,
        seats: (row.seats || []).map((seat) => {
          let guid = seat.seat_guid || `${newPrefix}-${row.row_label || row.row_number}-${seat.seat_number}`;
          if (guid.startsWith(oldPrefix + '-')) {
            guid = newPrefix + guid.substring(oldPrefix.length);
          } else if (guid.startsWith('Groundfloor-')) {
            guid = newPrefix + guid.substring('Groundfloor'.length);
          }
          return {
            ...seat,
            seat_guid: guid,
          };
        }),
      }));

      nextZones[zIdx] = {
        ...targetZone,
        name: data.name,
        id_for_seat_ids: data.id_for_seat_ids,
        rows: updatedRows,
      };
      pushHistory({ ...plan, zones: nextZones });
      toast({ title: 'Zone updated', status: 'success', duration: 2000 });
    },
    [plan, pushHistory, toast],
  );

  const handleMoveZoneUp = useCallback(
    (zIdx) => {
      if (!plan || zIdx <= 0) return;
      if (plan.zones[zIdx]?.is_locked || plan.zones[zIdx - 1]?.is_locked)
        return;
      const nextZones = [...plan.zones];
      const temp = nextZones[zIdx];
      nextZones[zIdx] = nextZones[zIdx - 1];
      nextZones[zIdx - 1] = temp;
      pushHistory({ ...plan, zones: nextZones });
      setActiveZoneIndex(zIdx - 1);
    },
    [plan, pushHistory],
  );

  const handleMoveZoneDown = useCallback(
    (zIdx) => {
      if (!plan || zIdx >= plan.zones.length - 1) return;
      if (plan.zones[zIdx]?.is_locked || plan.zones[zIdx + 1]?.is_locked)
        return;
      const nextZones = [...plan.zones];
      const temp = nextZones[zIdx];
      nextZones[zIdx] = nextZones[zIdx + 1];
      nextZones[zIdx + 1] = temp;
      pushHistory({ ...plan, zones: nextZones });
      setActiveZoneIndex(zIdx + 1);
    },
    [plan, pushHistory],
  );

  const handleToggleLockZone = useCallback(
    (zIdx) => {
      if (!plan || !plan.zones[zIdx]) return;
      const nextZones = [...plan.zones];
      nextZones[zIdx] = {
        ...nextZones[zIdx],
        is_locked: !nextZones[zIdx].is_locked,
      };
      pushHistory({ ...plan, zones: nextZones });
    },
    [plan, pushHistory],
  );

  const handleDeleteZone = useCallback(
    (zIdx) => {
      if (!plan || plan.zones.length <= 1) return;
      if (!window.confirm('Delete this zone and all objects inside it?'))
        return;
      const nextZones = plan.zones.filter((_, i) => i !== zIdx);
      pushHistory({ ...plan, zones: nextZones });
      setActiveZoneIndex(0);
      setSelectedRowIndex(null);
      setSelectedSeatGuids([]);
      setSelectedAreaIndices([]);
      toast({ title: 'Zone deleted', status: 'info', duration: 2000 });
    },
    [plan, pushHistory, toast],
  );

  // -------------------------------------------------------------
  // CATEGORIES & PLAN SETTINGS
  // -------------------------------------------------------------

  const handleAddCategory = useCallback(
    (cat) => {
      if (!plan) return;
      const nextCategories = [...(plan.categories || []), cat];
      pushHistory({ ...plan, categories: nextCategories });
      toast({
        title: `Category "${cat.name}" added`,
        status: 'success',
        duration: 2000,
      });
    },
    [plan, pushHistory, toast],
  );

  const handleUpdateCategory = useCallback(
    (index, updatedCat) => {
      if (!plan) return;
      const oldCatName = plan.categories[index]?.name;
      const nextCategories = [...plan.categories];
      nextCategories[index] = updatedCat;

      const nextZones = plan.zones.map((zone) => ({
        ...zone,
        rows: (zone.rows || []).map((row) => ({
          ...row,
          seats: (row.seats || []).map((seat) => {
            if (seat.category === oldCatName) {
              return { ...seat, category: updatedCat.name };
            }
            return seat;
          }),
        })),
      }));

      pushHistory({ ...plan, categories: nextCategories, zones: nextZones });
    },
    [plan, pushHistory],
  );

  const handleDeleteCategory = useCallback(
    (index) => {
      if (!plan) return;
      const catToDelete = plan.categories[index];
      const nextCategories = plan.categories.filter((_, i) => i !== index);
      pushHistory({ ...plan, categories: nextCategories });
      toast({
        title: `Category "${catToDelete?.name}" deleted`,
        status: 'info',
        duration: 2000,
      });
    },
    [plan, pushHistory, toast],
  );

  // -------------------------------------------------------------
  // KEYBOARD SHORTCUTS ENGINE
  // -------------------------------------------------------------
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger hotkeys if typing inside an input/textarea/select
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
        return;
      }

      // Ctrl / Cmd shortcuts
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'z' || e.key === 'Z') {
          e.preventDefault();
          if (e.shiftKey) {
            handleRedo();
          } else {
            handleUndo();
          }
        } else if (e.key === 'y' || e.key === 'Y') {
          e.preventDefault();
          handleRedo();
        } else if (e.key === 's' || e.key === 'S') {
          e.preventDefault();
          handleSave();
        } else if (e.key === 'c' || e.key === 'C') {
          e.preventDefault();
          handleCopy();
        } else if (e.key === 'x' || e.key === 'X') {
          e.preventDefault();
          handleCut();
        } else if (e.key === 'v' || e.key === 'V') {
          e.preventDefault();
          handlePaste();
        } else if (e.key === '-' || e.key === '_') {
          e.preventDefault();
          handleZoomOut();
        } else if (e.key === '=' || e.key === '+') {
          e.preventDefault();
          handleZoomIn();
        } else if (e.key === '0') {
          e.preventDefault();
          handleCenterView();
        }
      } else {
        // Direct single key shortcuts
        if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          handleDeleteSelection();
        } else if (e.key === 'Escape') {
          setActiveTool('select');
        } else if (e.key === 'v' || e.key === 'V') {
          setActiveTool('select');
        } else if (e.key === 's' || e.key === 'S') {
          setActiveTool('seat-select');
        } else if (e.key === 'b' || e.key === 'B') {
          setActiveTool('add-rows');
        } else if (e.key === 'n' || e.key === 'N') {
          setActiveTool('add-row');
        } else if (e.key === 'm' || e.key === 'M') {
          setActiveTool('add-seat');
        } else if (e.key === 'r' || e.key === 'R') {
          setActiveTool('add-rect');
        } else if (e.key === 'c' || e.key === 'C') {
          setActiveTool('add-circle');
        } else if (e.key === 'e' || e.key === 'E') {
          setActiveTool('add-ellipse');
        } else if (e.key === 'p' || e.key === 'P') {
          setActiveTool('add-polygon');
        } else if (e.key === 't' || e.key === 'T') {
          setActiveTool('add-text');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    handleDeleteSelection,
    handleUndo,
    handleRedo,
    handleSave,
    handleCopy,
    handleCut,
    handlePaste,
    handleZoomIn,
    handleZoomOut,
    handleCenterView,
  ]);

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      zIndex={1000}
      bg={useColorModeValue('#F1F5F9', '#0F172A')}
      display="flex"
      flexDirection="column"
    >
      {/* Print styles for high-res vector visual PDF export (single page, fit-to-paper, centered) */}
      <style>{`
        @media print {
          @page {
            size: auto;
            margin: 10mm;
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            height: 100% !important;
            background: #ffffff !important;
            overflow: hidden !important;
          }
          body * {
            visibility: hidden !important;
          }
          #seating-plan-print-svg,
          #seating-plan-print-svg * {
            visibility: visible !important;
          }
          #seating-plan-print-svg {
            display: block !important;
            position: fixed !important;
            left: 0 !important;
            top: 0 !important;
            width: 100vw !important;
            height: 96vh !important;
            max-width: 100vw !important;
            max-height: 96vh !important;
            page-break-inside: avoid !important;
            page-break-after: avoid !important;
            page-break-before: avoid !important;
          }
          .no-print,
          #seating-plan-canvas-svg {
            display: none !important;
            visibility: hidden !important;
          }
        }
        @media screen {
          #seating-plan-print-svg {
            display: none !important;
          }
        }
      `}</style>

      {/* Hidden File Input for JSON import */}
      <input
        type="file"
        ref={fileInputRef}
        accept=".json"
        style={{ display: 'none' }}
        onChange={handleImportJson}
      />

      {/* Top Header Bar */}
      <Flex
        h="52px"
        px={3}
        bg={topBarBg}
        borderBottom="1px solid"
        borderColor={borderColor}
        align="center"
        justify="space-between"
        boxShadow="sm"
        zIndex={20}
      >
        <HStack spacing={2}>
          <IconButton
            icon={<MdArrowBack />}
            aria-label="Back"
            size="sm"
            variant="ghost"
            onClick={handleBackNavigation}
          />
          <Input
            value={plan?.name || ''}
            onChange={(e) => setPlan({ ...plan, name: e.target.value })}
            fontWeight="700"
            fontSize="md"
            color={inputColor}
            maxW="260px"
            size="sm"
            variant="unstyled"
            placeholder="Plan Title..."
          />
          {validationErrors.length === 0 ? (
            <Badge
              colorScheme="green"
              display="flex"
              alignItems="center"
              gap={1}
              px={2}
              py={0.5}
              borderRadius="4px"
              fontSize="xs"
              cursor="pointer"
              onClick={onOpenValidation}
            >
              <MdCheckCircle /> Ready
            </Badge>
          ) : (
            <Badge
              colorScheme="red"
              display="flex"
              alignItems="center"
              gap={1}
              px={2}
              py={0.5}
              borderRadius="4px"
              fontSize="xs"
              cursor="pointer"
              onClick={onOpenValidation}
            >
              <MdWarning /> {validationErrors.length} conflict(s)
            </Badge>
          )}
        </HStack>

        <HStack spacing={2}>
          <Button
            size="sm"
            leftIcon={<MdSave />}
            colorScheme="brand"
            isLoading={updatePlanMutation.isPending}
            onClick={handleSave}
            borderRadius="6px"
          >
            Save Plan
          </Button>

          <IconButton
            icon={colorMode === 'light' ? <IoMdMoon /> : <IoMdSunny />}
            aria-label="Toggle Color Mode"
            size="sm"
            variant="solid"
            bg="linear-gradient(135deg, #868CFF 0%, #4318FF 100%)"
            color="white"
            _hover={{ bg: 'linear-gradient(135deg, #9F9FFF 0%, #5B32FF 100%)' }}
            onClick={toggleColorMode}
            borderRadius="full"
            title={
              colorMode === 'light'
                ? 'Switch to Dark Mode'
                : 'Switch to Light Mode'
            }
          />

          <IconButton
            icon={isFullscreen ? <MdFullscreenExit /> : <MdFullscreen />}
            aria-label="Toggle Fullscreen"
            size="sm"
            variant="ghost"
            onClick={() => setIsFullscreen(!isFullscreen)}
          />
        </HStack>
      </Flex>

      {/* Horizontal Toolbar */}
      <Toolbar
        activeTool={activeTool}
        setActiveTool={setActiveTool}
        onOpenAutoLabel={onOpenAutoLabel}
        onNewPlan={handleNewPlan}
        onOpenLoad={() => fileInputRef.current?.click()}
        onSave={handleSave}
        isSaving={updatePlanMutation.isPending}
        onExportJson={handleExportJson}
        onExportPdf={handleExportPdf}
        onOpenValidation={onOpenValidation}
        validationErrorCount={validationErrors.length}
        canUndo={historyIndex > 0}
        canRedo={historyIndex < history.length - 1}
        onUndo={handleUndo}
        onRedo={handleRedo}
        canCopy={
          selectedRowIndex !== null ||
          selectedSeatGuids.length > 0 ||
          selectedAreaIndices.length > 0
        }
        canPaste={clipboard !== null}
        onCut={handleCut}
        onCopy={handleCopy}
        onPaste={handlePaste}
        onDeleteSelected={handleDeleteSelection}
        hasSelection={
          selectedRowIndex !== null ||
          selectedSeatGuids.length > 0 ||
          selectedAreaIndices.length > 0
        }
        canAlign={
          (selectedAreaIndices.length + selectedSeatGuids.length + (selectedRowIndex !== null && selectedSeatGuids.length === 0 ? 1 : 0)) >= 2
        }
        onAlign={handleAlign}
        zoom={zoom}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onResetZoom={() => handleCenterView()}
        onCenterView={() => handleCenterView()}
        snapToGrid={snapToGrid}
        onToggleSnapToGrid={() => setSnapToGrid(!snapToGrid)}
      />

      {/* Main Interactive Canvas & Right Docked Inspector */}
      <Flex flex="1" position="relative" overflow="hidden">
        {/* Center SVG Canvas */}
        <Box flex="1" position="relative" h="100%">
          <Canvas
            plan={plan}
            activeZoneIndex={activeZoneIndex}
            activeTool={activeTool}
            selectedRowIndex={selectedRowIndex}
            selectedSeatGuids={selectedSeatGuids}
            selectedAreaIndices={selectedAreaIndices}
            onSelectRow={(rIdx) => {
              setSelectedRowIndex(rIdx);
              if (rIdx === null) {
                setSelectedSeatGuids([]);
              }
            }}
            onSelectSeats={setSelectedSeatGuids}
            onSelectAreas={setSelectedAreaIndices}
            onMoveRow={handleMoveRow}
            onTransformGroup={handleTransformGroup}
            onMoveSingleSeat={handleMoveSingleSeat}
            onMoveMultipleSeats={handleMoveMultipleSeats}
            onMoveArea={handleMoveArea}
            onCommitCreatedRow={handleCommitCreatedRow}
            onCommitCreatedRows={handleCommitCreatedRows}
            onCommitCreatedSeat={handleCommitCreatedSeat}
            onCommitCreatedArea={handleCommitCreatedArea}
            zoom={zoom}
            pan={pan}
            onPanChange={setPan}
            onZoomChange={setZoom}
            snapToGrid={snapToGrid}
            onFinishDrawing={() => setActiveTool('select')}
            onDragEnd={() => pushHistory(plan)}
          />
        </Box>

        {/* Right Docked Inspector Sidebar */}
        <Sidebar
          plan={plan}
          selectedRowIndex={selectedRowIndex}
          selectedSeatGuids={selectedSeatGuids}
          selectedAreaIndices={selectedAreaIndices}
          onOpenAutoLabel={onOpenAutoLabel}
          onSelectRow={(idx) => {
            setSelectedRowIndex(idx);
            setSelectedSeatGuids([]);
            setSelectedAreaIndices([]);
          }}
          onAssignCategory={(catName) => {
            if (selectedRowIndex !== null) {
              handleUpdateRowProperty(selectedRowIndex, 'category', catName);
            } else if (selectedSeatGuids.length === 1) {
              handleUpdateSingleSeatProperty(
                selectedSeatGuids[0],
                'category',
                catName,
              );
            } else if (selectedSeatGuids.length > 1) {
              handleBatchUpdateSeats(selectedSeatGuids, { category: catName });
            }
          }}
          onBatchUpdateSeats={handleBatchUpdateSeats}
          onDeleteSelectedSeats={handleDeleteSelection}
          onUpdateRowProperty={handleUpdateRowProperty}
          onAddSeatToRow={handleAddSeatToRow}
          onRemoveSeatFromRow={handleRemoveSeatFromRow}
          onUpdateSingleSeatProperty={handleUpdateSingleSeatProperty}
          onUpdateArea={handleUpdateArea}
          onDeleteArea={handleDeleteArea}
          onReorderAreaLayer={handleReorderAreaLayer}
          onAddCategory={handleAddCategory}
          onUpdateCategory={handleUpdateCategory}
          onDeleteCategory={handleDeleteCategory}
          onAddZone={handleAddZone}
          onEditZone={handleEditZone}
          onMoveZoneUp={handleMoveZoneUp}
          onMoveZoneDown={handleMoveZoneDown}
          onToggleLockZone={handleToggleLockZone}
          onDeleteZone={handleDeleteZone}
          activeZoneIndex={activeZoneIndex}
          setActiveZoneIndex={(zIdx) => {
            // Clear selection when switching zones to prevent stale index pointing to wrong zone's rows
            setSelectedRowIndex(null);
            setSelectedSeatGuids([]);
            setSelectedAreaIndices([]);
            setActiveZoneIndex(zIdx);
          }}
          onUpdateBackground={(bgUpdate) => {
            pushHistory({ ...plan, ...bgUpdate });
          }}
          onUpdatePlanDimensions={(width, height) => {
            pushHistory({
              ...plan,
              size: { width, height },
            });
          }}
          onUpdatePlanName={(name) => {
            setPlan({ ...plan, name });
          }}
        />
      </Flex>

      {/* Validation Result Modal */}
      <ValidationModal
        isOpen={isValidationOpen}
        onClose={onCloseValidation}
        errors={validationErrors}
        onAutoResolve={() => {
          if (!plan) return;
          const resolved = autoResolveDuplicateRowNumbers(plan);
          pushHistory(resolved);
          toast({
            title: 'Duplicate row conflicts auto-resolved',
            description:
              'All rows assigned unique row numbers and seat IDs updated.',
            status: 'success',
            duration: 3000,
          });
        }}
      />

      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        isOpen={isUnsavedModalOpen}
        onClose={onCloseUnsavedModal}
        onSaveAndExit={handleSaveAndExit}
        onDiscardAndExit={handleDiscardAndExit}
        isSaving={updatePlanMutation.isPending}
      />

      {/* Auto Label Rows & Number Seats Modal */}
      <AutoLabelModal
        isOpen={isAutoLabelOpen}
        onClose={onCloseAutoLabel}
        plan={plan}
        activeZoneIndex={activeZoneIndex}
        selectedRowIndices={selectedRowIndices}
        onApply={handleApplyAutoLabels}
      />
    </Box>
  );
}
